import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

var PRIORITY_COLOR = {
  critical: { bg: 'rgba(239,68,68,0.12)', color: '#EF4444', label: 'קריטי' },
  high:     { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B', label: 'גבוה' },
  medium:   { bg: 'rgba(59,130,246,0.12)', color: '#3B82F6', label: 'בינוני' },
  low:      { bg: 'rgba(34,197,94,0.12)', color: '#22C55E', label: 'נמוך' },
};

var EFFORT_COLOR = { 'נמוך': '#22C55E', 'בינוני': '#F59E0B', 'גבוה': '#EF4444' };

export default function MonthlyPlan() {
  var params = useParams();
  var id = params.id;
  var navigate = useNavigate();

  var [site, setSite] = useState(null);
  var [plan, setPlan] = useState(null);
  var [plans, setPlans] = useState([]);
  var [selectedMonth, setSelectedMonth] = useState('');
  var [loading, setLoading] = useState(true);
  var [generating, setGenerating] = useState(false);
  var [message, setMessage] = useState(null);
  var [activeTab, setActiveTab] = useState('priorities');
  var [showAddTask, setShowAddTask] = useState(false);
  var [newTask, setNewTask] = useState({ description: '', priority: 'medium', type: 'manual', target_keyword: '' });
  var [sendingToChecklist, setSendingToChecklist] = useState(false);

  var monthOptions = (function() {
    var opts = [];
    var now = new Date();
    for (var i = 0; i < 12; i++) {
      var d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      var val = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      var label = d.toLocaleDateString('he-IL', { year: 'numeric', month: 'long' });
      opts.push({ val: val, label: label });
    }
    return opts;
  })();

  useEffect(function() { loadInitial(); }, [id]);

  async function loadInitial() {
    setLoading(true);
    try {
      var results = await Promise.all([api.getSite(id), api.getMonthlyPlans(id)]);
      setSite(results[0]);
      setPlans(results[1]);
      var currentMonth = monthOptions[0].val;
      setSelectedMonth(currentMonth);
      var current = results[1].find(function(p) { return p.month === currentMonth; });
      if (current) setPlan(current);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function loadPlan(month) {
    setSelectedMonth(month);
    var cached = plans.find(function(p) { return p.month === month; });
    if (cached) { setPlan(cached); return; }
    try { var data = await api.getMonthlyPlan(id, month); setPlan(data); }
    catch (e) { setPlan(null); }
  }

  async function handleGenerate() {
    setGenerating(true); setMessage(null);
    try {
      var data = await api.generateMonthlyPlan(id, selectedMonth);
      setPlan(data);
      setPlans(function(prev) {
        return [data].concat(prev.filter(function(p) { return p.month !== selectedMonth; }));
      });
      setMessage({ type: 'success', text: 'תוכנית נוצרה בהצלחה' });
    } catch (err) {
      setMessage({ type: 'error', text: 'שגיאה ביצירת תוכנית: ' + err.message });
    }
    setGenerating(false);
  }

  async function handleAddManualTask() {
    if (!newTask.description.trim()) return;
    var updated = Object.assign({}, plan);
    var manualTasks = (updated.content_tasks || []).slice();
    manualTasks.push({
      type: 'manual',
      title: newTask.description,
      description: newTask.notes || '',
      target_keyword: newTask.target_keyword || '',
      priority: newTask.priority,
      _manual: true,
    });
    updated.content_tasks = manualTasks;
    try {
      await api.updateMonthlyPlan(id, selectedMonth, { content_tasks: manualTasks });
      setPlan(updated);
      setNewTask({ description: '', priority: 'medium', type: 'manual', target_keyword: '' });
      setShowAddTask(false);
      setMessage({ type: 'success', text: 'משימה נוספה בהצלחה' });
    } catch (err) {
      setMessage({ type: 'error', text: 'שגיאה: ' + err.message });
    }
  }

  async function handleSendToChecklist() {
    if (!plan) return;
    setSendingToChecklist(true); setMessage(null);
    var tasks = [];

    // Priorities -> tasks
    (plan.priorities || []).forEach(function(p) {
      tasks.push({
        site_id: parseInt(id),
        type: 'monthly_priority',
        description: p.title + (p.description ? ' - ' + p.description : ''),
        priority: p.rank <= 1 ? 'critical' : 'high',
        source: 'monthly_plan',
        source_keyword: (p.keywords_affected || [])[0] || null,
        notes: 'תוכנית ' + selectedMonth + ' | יעד: ' + (p.expected_impact || '-'),
      });
    });

    // Content tasks -> tasks
    (plan.content_tasks || []).forEach(function(t) {
      tasks.push({
        site_id: parseInt(id),
        type: t.type || 'content',
        description: t.title + (t.description ? ' - ' + t.description : ''),
        priority: t.priority || 'medium',
        source: 'monthly_plan',
        source_keyword: t.target_keyword || null,
        notes: 'תוכנית ' + selectedMonth,
      });
    });

    // Technical tasks -> tasks
    (plan.technical_tasks || []).forEach(function(t) {
      tasks.push({
        site_id: parseInt(id),
        type: 'technical',
        description: t.title + (t.description ? ' - ' + t.description : ''),
        priority: t.priority || 'medium',
        source: 'monthly_plan',
        notes: 'תוכנית ' + selectedMonth,
      });
    });

    // Striking distance -> tasks
    (plan.striking_distance_actions || []).forEach(function(a) {
      tasks.push({
        site_id: parseInt(id),
        type: 'striking_distance',
        description: a.keyword + ' (מיקום ' + a.current_position + ') - ' + a.action,
        priority: 'high',
        source: 'monthly_plan',
        source_keyword: a.keyword,
        notes: 'תוכנית ' + selectedMonth + ' | Striking Distance',
      });
    });

    // Drop recovery -> tasks
    (plan.drop_recovery || []).forEach(function(a) {
      tasks.push({
        site_id: parseInt(id),
        type: 'drop_recovery',
        description: a.keyword + ' - ' + a.action,
        priority: 'critical',
        source: 'monthly_plan',
        source_keyword: a.keyword,
        notes: 'תוכנית ' + selectedMonth + ' | סיבה: ' + (a.suspected_reason || 'לא ידועה'),
      });
    });

    if (tasks.length === 0) {
      setMessage({ type: 'error', text: 'אין משימות לשליחה' });
      setSendingToChecklist(false);
      return;
    }

    try {
      var result = await api.createTasksBulk(tasks);
      setMessage({ type: 'success', text: 'נוצרו ' + tasks.length + ' משימות ב-Checklist בהצלחה' });
    } catch (err) {
      setMessage({ type: 'error', text: 'שגיאה בשליחה ל-Checklist: ' + err.message });
    }
    setSendingToChecklist(false);
  }

  if (loading) return React.createElement('div', { className: 'loading' }, 'טוען...');
  if (!site) return React.createElement('div', { className: 'loading' }, 'אתר לא נמצא');

  var monthLabel = (monthOptions.find(function(o) { return o.val === selectedMonth; }) || {}).label || selectedMonth;
  var meta = (plan && plan._meta) || {};
  var summary = meta.rankings_summary || {};

  var totalTasks = plan ? (plan.priorities || []).length + (plan.content_tasks || []).length + (plan.technical_tasks || []).length + (plan.striking_distance_actions || []).length + (plan.drop_recovery || []).length : 0;

  return React.createElement('div', null,

    // Back button
    React.createElement('button', { className: 'back-btn', onClick: function() { navigate('/sites/' + id); } }, '\u2192 חזרה ל-' + site.name),

    // Header
    React.createElement('div', { className: 'page-header' },
      React.createElement('div', null,
        React.createElement('div', { className: 'page-title' }, 'תוכנית עבודה חודשית'),
        React.createElement('div', { className: 'page-subtitle', style: { direction: 'ltr', textAlign: 'right' } }, site.url)
      ),
      React.createElement('div', { style: { display: 'flex', gap: '8px', alignItems: 'center' } },
        React.createElement('select', {
          value: selectedMonth,
          onChange: function(e) { loadPlan(e.target.value); },
          style: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', padding: '8px 12px', fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer' }
        }, monthOptions.map(function(o) {
          var hasPlan = plans.find(function(p) { return p.month === o.val; });
          return React.createElement('option', { key: o.val, value: o.val }, o.label + (hasPlan ? ' \u2713' : ''));
        })),
        React.createElement('button', {
          className: 'btn btn-primary', onClick: handleGenerate, disabled: generating
        }, generating ? 'יוצר תוכנית...' : plan ? 'יצירה מחדש' : 'צור תוכנית')
      )
    ),

    // Message
    message && React.createElement('div', {
      style: {
        background: message.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
        border: '1px solid ' + (message.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'),
        borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: '14px',
        color: message.type === 'error' ? '#EF4444' : '#22C55E',
      }
    }, message.text),

    // Empty state
    !plan ? React.createElement('div', {
      style: { textAlign: 'center', padding: '60px 24px', border: '1px dashed var(--border)', borderRadius: '12px' }
    },
      React.createElement('div', { style: { fontSize: '40px', marginBottom: '16px' } }, '\uD83D\uDCCB'),
      React.createElement('div', { style: { fontSize: '18px', fontWeight: 700, marginBottom: '8px' } }, 'אין תוכנית עבודה ל-' + monthLabel),
      React.createElement('div', { style: { fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' } }, 'Claude יאסוף נתוני מיקומים, Search Console ו-Analytics ויבנה תוכנית מותאמת'),
      React.createElement('button', { className: 'btn btn-primary', onClick: handleGenerate, disabled: generating }, generating ? 'יוצר תוכנית...' : 'צור תוכנית עכשיו')
    ) :

    // Plan content
    React.createElement(React.Fragment, null,

      // Parse-error banner (generation produced invalid JSON)
      plan.parse_error && React.createElement('div', {
        style: {
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: '#EF4444'
        }
      },
        React.createElement('div', { style: { fontWeight: 700, marginBottom: '4px' } }, '⚠️ הפקת התוכנית נכשלה'),
        React.createElement('div', { style: { fontSize: '13px' } }, 'התשובה מ-Claude לא הוחזרה כ-JSON תקין. נסה להפיק מחדש.'),
        React.createElement('button', { className: 'btn btn-primary', onClick: handleGenerate, disabled: generating, style: { marginTop: '10px' } }, generating ? 'יוצר תוכנית...' : 'הפק מחדש')
      ),

      // Stats row
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' } },
        [
          { val: summary.total || '-', label: 'ביטויים', color: '#3B82F6' },
          { val: summary.top10 || '-', label: 'Top 10', color: '#22C55E' },
          { val: summary.improved || '-', label: 'עלו', color: '#22C55E' },
          { val: summary.declined || '-', label: 'ירדו', color: '#EF4444' },
          { val: meta.striking_distance_count || '-', label: 'Striking Distance', color: '#F59E0B' },
        ].map(function(s, i) {
          return React.createElement('div', {
            key: i,
            style: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', textAlign: 'center' }
          },
            React.createElement('div', { style: { fontSize: '28px', fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono', monospace" } }, s.val),
            React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' } }, s.label)
          );
        })
      ),

      // Executive summary
      plan.executive_summary && React.createElement('div', {
        style: { background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }
      },
        React.createElement('div', { style: { fontSize: '12px', color: 'var(--accent)', fontWeight: 600, marginBottom: '6px' } }, 'סיכום חודשי - ' + monthLabel),
        React.createElement('div', { style: { fontSize: '14px', lineHeight: 1.7 } }, plan.executive_summary),
        plan.month_highlight && React.createElement('div', { style: { marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(59,130,246,0.15)', fontSize: '13px', color: 'var(--text-secondary)' } }, plan.month_highlight)
      ),

      // Tabs
      React.createElement('div', { style: { display: 'flex', gap: '0', borderBottom: '1px solid var(--border)', marginBottom: '20px', overflowX: 'auto' } },
        [
          { key: 'priorities', label: 'עדיפויות (' + (plan.priorities || []).length + ')' },
          { key: 'content', label: 'תוכן (' + (plan.content_tasks || []).length + ')' },
          { key: 'technical', label: 'טכני (' + (plan.technical_tasks || []).length + ')' },
          { key: 'striking', label: 'Striking (' + (plan.striking_distance_actions || []).length + ')' },
          { key: 'drops', label: 'שחזור (' + (plan.drop_recovery || []).length + ')' },
        ].map(function(tab) {
          return React.createElement('button', {
            key: tab.key,
            onClick: function() { setActiveTab(tab.key); },
            style: {
              padding: '10px 16px', fontSize: '13px', fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
              background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
              borderBottom: '2px solid ' + (activeTab === tab.key ? 'var(--accent)' : 'transparent'),
            }
          }, tab.label);
        })
      ),

      // Tab content
      activeTab === 'priorities' && React.createElement(PrioritiesTab, { priorities: plan.priorities }),
      activeTab === 'content' && React.createElement(ContentTasksTab, { tasks: plan.content_tasks, showAddTask: showAddTask, setShowAddTask: setShowAddTask, newTask: newTask, setNewTask: setNewTask, onAdd: handleAddManualTask }),
      activeTab === 'technical' && React.createElement(TechnicalTasksTab, { tasks: plan.technical_tasks }),
      activeTab === 'striking' && React.createElement(StrikingTab, { actions: plan.striking_distance_actions }),
      activeTab === 'drops' && React.createElement(DropsTab, { actions: plan.drop_recovery }),

      // Footer actions
      React.createElement('div', {
        style: { marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }
      },
        // Left side - send to checklist
        React.createElement('div', { style: { display: 'flex', gap: '8px', alignItems: 'center' } },
          React.createElement('button', {
            className: 'btn btn-primary',
            onClick: handleSendToChecklist,
            disabled: sendingToChecklist || totalTasks === 0,
            style: { display: 'flex', alignItems: 'center', gap: '6px' }
          }, sendingToChecklist ? 'שולח...' : '\u2705 שלח ' + totalTasks + ' משימות ל-Checklist'),
          React.createElement('button', {
            className: 'btn btn-secondary',
            onClick: function() { navigate('/sites/' + id + '/checklist'); }
          }, 'פתח Checklist')
        ),
        // Right side - mark sent
        React.createElement('div', { style: { display: 'flex', gap: '8px', alignItems: 'center' } },
          !plan.sent_to_client && React.createElement('button', {
            className: 'btn btn-secondary',
            onClick: async function() {
              await api.markPlanSent(id, selectedMonth);
              setPlan(function(p) { return Object.assign({}, p, { sent_to_client: true }); });
            }
          }, 'סמן כנשלח ללקוח'),
          plan.sent_to_client && React.createElement('span', { style: { fontSize: '13px', color: 'var(--green)' } }, '\u2713 נשלח ללקוח')
        )
      )
    )
  );
}

// ── Priorities Tab ──
function PrioritiesTab(props) {
  var priorities = props.priorities || [];
  if (!priorities.length) return React.createElement(Empty, { text: 'אין עדיפויות בתוכנית' });
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
    priorities.map(function(p, i) {
      return React.createElement('div', {
        key: i,
        style: { border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px', background: 'var(--surface)' }
      },
        React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: '12px' } },
          React.createElement('div', {
            style: {
              width: '32px', height: '32px', borderRadius: '50%',
              background: i === 0 ? 'rgba(59,130,246,0.15)' : 'var(--surface-hover)',
              color: i === 0 ? 'var(--accent)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '14px', flexShrink: 0,
            }
          }, p.rank || (i + 1)),
          React.createElement('div', { style: { flex: 1 } },
            React.createElement('div', { style: { fontWeight: 700, fontSize: '15px', marginBottom: '6px' } }, p.title),
            React.createElement('div', { style: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '10px' } }, p.description),
            React.createElement('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
              p.expected_impact && React.createElement(Tag, { text: 'יעד: ' + p.expected_impact, color: 'var(--green)' }),
              p.effort && React.createElement(Tag, { text: 'מאמץ: ' + p.effort, color: EFFORT_COLOR[p.effort] || 'var(--text-secondary)' }),
              (p.keywords_affected || []).map(function(kw, j) { return React.createElement(Tag, { key: j, text: kw, color: 'var(--accent)' }); })
            )
          )
        )
      );
    })
  );
}

// ── Content Tasks Tab (with manual add) ──
function ContentTasksTab(props) {
  var tasks = props.tasks || [];
  var typeLabel = { new_article: 'מאמר חדש', update_existing: 'עדכון', add_faq: 'FAQ', add_schema: 'Schema', manual: 'ידני' };

  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },

    // Task list
    tasks.length === 0 && !props.showAddTask && React.createElement(Empty, { text: 'אין משימות תוכן' }),

    tasks.map(function(t, i) {
      var p = PRIORITY_COLOR[t.priority] || PRIORITY_COLOR.medium;
      return React.createElement('div', {
        key: i,
        style: { border: '1px solid var(--border)', borderRadius: '8px', padding: '14px 16px', background: 'var(--surface)', display: 'flex', gap: '12px', alignItems: 'flex-start' }
      },
        React.createElement('span', { style: { fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: p.bg, color: p.color, fontWeight: 600, flexShrink: 0, marginTop: '2px' } }, p.label),
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontWeight: 600, fontSize: '14px', marginBottom: '4px' } }, t.title),
          t.description && React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-secondary)' } }, t.description),
          t.target_keyword && React.createElement('div', { style: { marginTop: '6px' } }, React.createElement(Tag, { text: 'ביטוי: ' + t.target_keyword, color: 'var(--accent)' }))
        ),
        React.createElement('span', { style: { fontSize: '11px', color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '3px 8px', borderRadius: '6px', flexShrink: 0 } }, typeLabel[t.type] || t.type)
      );
    }),

    // Add task form
    props.showAddTask && React.createElement('div', {
      style: { border: '1px solid var(--accent)', borderRadius: '8px', padding: '16px', background: 'rgba(59,130,246,0.05)' }
    },
      React.createElement('div', { style: { fontSize: '14px', fontWeight: 600, marginBottom: '12px' } }, 'הוספת משימה ידנית'),
      React.createElement('input', {
        placeholder: 'תיאור המשימה',
        value: props.newTask.description,
        onChange: function(e) { props.setNewTask(Object.assign({}, props.newTask, { description: e.target.value })); },
        style: { width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--surface)', color: 'var(--text)', fontSize: '13px', fontFamily: 'inherit', marginBottom: '8px', boxSizing: 'border-box' }
      }),
      React.createElement('div', { style: { display: 'flex', gap: '8px', marginBottom: '8px' } },
        React.createElement('input', {
          placeholder: 'ביטוי יעד (אופציונלי)',
          value: props.newTask.target_keyword,
          onChange: function(e) { props.setNewTask(Object.assign({}, props.newTask, { target_keyword: e.target.value })); },
          style: { flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--surface)', color: 'var(--text)', fontSize: '13px', fontFamily: 'inherit' }
        }),
        React.createElement('select', {
          value: props.newTask.priority,
          onChange: function(e) { props.setNewTask(Object.assign({}, props.newTask, { priority: e.target.value })); },
          style: { padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--surface)', color: 'var(--text)', fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer' }
        },
          React.createElement('option', { value: 'critical' }, 'קריטי'),
          React.createElement('option', { value: 'high' }, 'גבוה'),
          React.createElement('option', { value: 'medium' }, 'בינוני'),
          React.createElement('option', { value: 'low' }, 'נמוך')
        )
      ),
      React.createElement('div', { style: { display: 'flex', gap: '8px' } },
        React.createElement('button', { className: 'btn btn-primary', onClick: props.onAdd, style: { fontSize: '13px' } }, 'הוסף'),
        React.createElement('button', { className: 'btn btn-secondary', onClick: function() { props.setShowAddTask(false); }, style: { fontSize: '13px' } }, 'ביטול')
      )
    ),

    // Add button
    !props.showAddTask && React.createElement('button', {
      onClick: function() { props.setShowAddTask(true); },
      style: {
        border: '1px dashed var(--border)', borderRadius: '8px', padding: '12px',
        background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px',
        fontFamily: 'inherit', width: '100%', textAlign: 'center',
      }
    }, '+ הוספת משימה ידנית')
  );
}

// ── Technical Tasks Tab ──
function TechnicalTasksTab(props) {
  var tasks = props.tasks || [];
  if (!tasks.length) return React.createElement(Empty, { text: 'אין משימות טכניות' });
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
    tasks.map(function(t, i) {
      var p = PRIORITY_COLOR[t.priority] || PRIORITY_COLOR.medium;
      return React.createElement('div', {
        key: i,
        style: { border: '1px solid var(--border)', borderRadius: '8px', padding: '14px 16px', background: 'var(--surface)', display: 'flex', gap: '12px', alignItems: 'flex-start' }
      },
        React.createElement('span', { style: { fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: p.bg, color: p.color, fontWeight: 600, flexShrink: 0, marginTop: '2px' } }, p.label),
        React.createElement('div', null,
          React.createElement('div', { style: { fontWeight: 600, fontSize: '14px', marginBottom: '4px' } }, t.title),
          React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-secondary)' } }, t.description)
        )
      );
    })
  );
}

// ── Striking Distance Tab ──
function StrikingTab(props) {
  var actions = props.actions || [];
  if (!actions.length) return React.createElement(Empty, { text: 'אין ביטויים ב-Striking Distance' });
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
    actions.map(function(a, i) {
      return React.createElement('div', {
        key: i,
        style: { border: '1px solid rgba(245,158,11,0.25)', borderRadius: '8px', padding: '14px 16px', background: 'rgba(245,158,11,0.05)', display: 'flex', gap: '16px', alignItems: 'center' }
      },
        React.createElement('div', { style: { fontFamily: "'JetBrains Mono', monospace", fontSize: '22px', fontWeight: 800, color: '#F59E0B', minWidth: '40px', textAlign: 'center' } }, a.current_position),
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontWeight: 700, marginBottom: '4px' } }, a.keyword),
          React.createElement('div', { style: { fontSize: '13px', color: 'var(--text-secondary)' } }, a.action)
        )
      );
    })
  );
}

// ── Drop Recovery Tab ──
function DropsTab(props) {
  var actions = props.actions || [];
  if (!actions.length) return React.createElement(Empty, { text: 'אין ירידות שדורשות שחזור' });
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
    actions.map(function(a, i) {
      return React.createElement('div', {
        key: i,
        style: { border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '14px 16px', background: 'rgba(239,68,68,0.04)' }
      },
        React.createElement('div', { style: { fontWeight: 700, marginBottom: '4px' } }, a.keyword),
        a.suspected_reason && React.createElement('div', { style: { fontSize: '12px', color: '#F59E0B', marginBottom: '6px' } }, 'סיבה אפשרית: ' + a.suspected_reason),
        React.createElement('div', { style: { fontSize: '13px', color: 'var(--text-secondary)' } }, a.action)
      );
    })
  );
}

// ── Helpers ──
function Tag(props) {
  return React.createElement('span', {
    style: { fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: props.color + '18', color: props.color, fontWeight: 500 }
  }, props.text);
}

function Empty(props) {
  return React.createElement('div', { style: { textAlign: 'center', padding: '32px', color: 'var(--text-secondary)', fontSize: '14px' } }, props.text);
}